import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  // Carregar usuário ao iniciar (se tiver token)
  useEffect(() => {
    const initAuth = async () => {
      // CRITICAL: If returning from OAuth callback, skip the /me check.
      // GoogleAuthCallback will exchange the session_id and establish the session first.
      if (window.location.hash?.includes('session_id=') || window.location.pathname.includes('/auth/google/callback')) {
        setLoading(false);
        return;
      }
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        setToken(storedToken);
        await loadUser();
      } else {
        setLoading(false);
      }
    };
    
    initAuth();
  }, []);

  const loadUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Token inválido ou expirado
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, remember_me = false) => {
    const response = await authAPI.login({ email, password, remember_me });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);

    // Apply user's saved locale if exists
    try {
      const { default: i18n } = await import('../i18n');
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Save timezone on login
      await api.patch('/locale/user', { locale: i18n.language, timezone: tz }, {
        headers: { Authorization: `Bearer ${access_token}` },
      }).catch(() => {});
    } catch { /* ignore */ }
    
    return userData;
  };

  const register = async (email, password, name, userType = 'user', referralCode = '') => {
    const response = await authAPI.register({ 
      email, 
      password, 
      name,
      user_type: userType,
      referral_code: referralCode || undefined
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('uclean_role_choice');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const setAuthData = ({ token: newToken, user: userData }) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user && !!token,
        login,
        register,
        logout,
        updateUser,
        refreshUser,
        setAuthData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
